/**
 * Custom header — wires visible buttons to the theme's native menu/cart drawers.
 */
class CustomHeader extends HTMLElement {
  connectedCallback() {
    this.menuButton = this.querySelector('[data-custom-header-menu]');
    this.countEls = this.querySelectorAll('[data-custom-header-cart-count]');

    this.onScroll = this.onScroll.bind(this);
    this.syncMenuState = this.syncMenuState.bind(this);
    this.onCartUpdate = this.onCartUpdate.bind(this);

    this.addEventListener('click', this.onRootClick, true);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('cart:update', this.onCartUpdate);

    this.bindNativeDrawer();
    this.ensureCartAutoOpen();
    this.onScroll();

    if (this.dataset.sticky === 'scroll-up') {
      this.classList.add('is-sticky-visible');
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onRootClick, true);
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('cart:update', this.onCartUpdate);
    this.drawerObserver?.disconnect();
  }

  onRootClick = (event) => {
    if (event.target.closest('[data-custom-header-menu]')) {
      this.onMenuClick(event);
      return;
    }

    if (event.target.closest('[data-custom-header-cart]')) {
      this.onCartClick(event);
      return;
    }

    if (event.target.closest('[data-custom-header-search]')) {
      this.onSearchClick(event);
    }
  };

  get drawersHost() {
    return (
      this.parentElement?.querySelector('[data-custom-header-drawers]') ||
      document.querySelector('[data-custom-header-drawers]') ||
      document.querySelector('#header-component') ||
      document
    );
  }

  get nativeDrawer() {
    return this.drawersHost.querySelector('header-drawer');
  }

  get nativeDrawerDetails() {
    return this.drawersHost.querySelector('#Details-menu-drawer-container');
  }

  get nativeCartDrawer() {
    return this.drawersHost.querySelector('cart-drawer-component');
  }

  get nativeCartTrigger() {
    return this.drawersHost.querySelector('[data-testid="cart-drawer-trigger"]');
  }

  get nativeSearchModal() {
    return document.querySelector('#search-modal');
  }

  isDrawerOpen() {
    const details = this.nativeDrawerDetails;
    if (!details) return false;
    return details.classList.contains('menu-open') || details.hasAttribute('open');
  }

  bindNativeDrawer() {
    const details = this.nativeDrawerDetails;
    if (!details) return;

    this.drawerObserver = new MutationObserver(this.syncMenuState);
    this.drawerObserver.observe(details, { attributes: true, attributeFilter: ['open', 'class'] });
    this.syncMenuState();
  }

  ensureCartAutoOpen() {
    const cartDrawer = this.nativeCartDrawer;
    if (cartDrawer && this.dataset.cartType === 'drawer') {
      cartDrawer.setAttribute('auto-open', '');
    }
  }

  syncMenuState() {
    const open = this.isDrawerOpen();
    this.classList.toggle('is-menu-open', open);
    this.menuButton?.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  async onMenuClick(event) {
    event.preventDefault();
    event.stopPropagation();

    await customElements.whenDefined('header-drawer');

    const drawer = this.nativeDrawer;
    const details = this.nativeDrawerDetails;

    if (!drawer || !details) return;

    if (this.isDrawerOpen()) {
      drawer.close();
    } else {
      details.setAttribute('open', '');
      drawer.open(undefined, event);
    }

    requestAnimationFrame(() => this.syncMenuState());
  }

  async onSearchClick(event) {
    event.preventDefault();
    event.stopPropagation();

    await customElements.whenDefined('dialog-component');

    const modal = this.nativeSearchModal;
    if (modal && typeof modal.showDialog === 'function') {
      modal.showDialog();
      return;
    }

    document.querySelector('#header-component .search-action button, search-button button')?.click();
  }

  async onCartClick(event) {
    if (this.dataset.cartType !== 'drawer') return;

    event.preventDefault();
    event.stopPropagation();

    await this.openCartDrawer();
  }

  async openCartDrawer() {
    if (this.dataset.cartType !== 'drawer') return false;

    await customElements.whenDefined('cart-drawer-component');

    const cartDrawer = this.nativeCartDrawer;

    if (cartDrawer && typeof cartDrawer.showDialog === 'function') {
      cartDrawer.showDialog();
      return true;
    }

    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return true;
    }

    const trigger = this.nativeCartTrigger;
    if (trigger) {
      trigger.click();
      return true;
    }

    return false;
  }

  onCartUpdate(event) {
    const detail = event?.detail;
    const count = detail?.resource?.item_count ?? detail?.data?.itemCount;

    if (count !== undefined && count !== null) {
      this.countEls.forEach((el) => {
        el.textContent = String(count);
      });
    }

    if (this.dataset.cartType !== 'drawer') return;
    if (detail?.data?.didError) return;

    const source = detail?.data?.source;
    const isAdd =
      source === 'product-form-component' ||
      source === 'quick-add' ||
      source === 'quick-order-list' ||
      (count !== undefined && count !== null && count > (this.lastCartCount ?? 0));

    this.lastCartCount = count ?? this.lastCartCount;

    if (isAdd) {
      this.openCartDrawer();
    }
  }

  onScroll() {
    const sticky = this.dataset.sticky;
    const scrolled = window.scrollY > 8;

    if (this.dataset.transparent === 'true') {
      this.classList.toggle('is-scrolled', scrolled);
    }

    if (sticky === 'scroll-up') {
      const current = window.scrollY;
      const goingUp = current < (this.lastScrollY || 0);
      this.classList.toggle('is-sticky-visible', goingUp || current < 16);
      this.lastScrollY = current;
    }
  }
}

if (!customElements.get('custom-header')) {
  customElements.define('custom-header', CustomHeader);
}
